import React from 'react';
import './Sidebar.css';

const Sidebar = ({
  isOpen,
  requestConfig,
  setRequestConfig,
  availableFields,
  keywordWeight,
  setKeywordWeight,
  handleSendWeight,
  showSuccess
}) => {
  function updateRequestConfig(key, value) {
    setRequestConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  const toggleSearchField = (field) => {
    setRequestConfig((prev) => {
      const isSelected = prev.selectedFields.includes(field);
  
      return {
        ...prev,
        selectedFields: isSelected
          ? prev.selectedFields.filter((selectedField) => selectedField !== field)
          : [...prev.selectedFields, field],
      };
    });
  };

  function handleBoostChange(field, value) {
    const boost = Math.max(1, Number(value));

    setRequestConfig((prev) => ({
      ...prev,
      fieldBoosts: {
        ...prev.fieldBoosts,
        [field]: boost,
      },
    }));
  }

  function addFilter(type) {
    setRequestConfig((prev) => ({
      ...prev,
      [type]: [...(prev[type] ?? []), { key: "", value: "" }],
    }));
  }
  
  function updateFilter(type, index, key, value) {
    setRequestConfig((prev) => ({
      ...prev,
      [type]: (prev[type] ?? []).map((filter, i) =>
        i === index ? { key, value } : filter
      ),
    }));
  }
  
  function removeFilter(type, indexToRemove) {
    setRequestConfig((prev) => ({
      ...prev,
      [type]: (prev[type] ?? []).filter((_, i) => i !== indexToRemove),
    }));
  }

  function updateSliderValue(value) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return;
    }
    const nextValue = Math.min(10000, Math.max(0, numericValue));

    setRequestConfig((prev) => ({
      ...prev,
      sliderValue: nextValue,
    }));
  }


  return (
    <div className={`Sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <h2>Developer Menü</h2>
      </div>

      <div className="sidebar-content">
        <label htmlFor="slider">Similarity Threshold:</label>
        <div className="slider-container">
          <div className="slider-input-wrapper">
            <input
              type="number"
              className="slider-number-input"
              min="0"
              max="10000"
              step="1"
              value={requestConfig.sliderValue}
              onChange={(e) => updateSliderValue(e.target.value)}
            />
          </div>
          <div className="slider-control">
            <button
              type="button"
              onClick={() => updateSliderValue(requestConfig.sliderValue - 1000)}
            >
              –
            </button>
            <input
              type="range"
              id="slider"
              min="0"
              max="10000"
              step="1"
              value={requestConfig.sliderValue}
              onChange={(e) => updateSliderValue(e.target.value)}
            />
            <button
              type="button"
              onClick={() => updateSliderValue(requestConfig.sliderValue + 1000)}
            >
              +
            </button>
          </div>
        </div>

        <label htmlFor="maxResults">Maximale Anzahl Ergebnisse:</label>
        <input
          type="number"
          id="maxResults"
          className="maxResults-number-input"
          min="1"
          value={requestConfig.maxResults}
          onChange={(e) =>
            updateRequestConfig("maxResults", Number(e.target.value))
          }
        />
      </div>

      <div className="hybrid-section">
        <h3>Hybrid Search Parameter</h3>

        <label className="section-label">Keyword vs. Semantik Gewichtung:</label>
        <div className="keyword-weight">
          <div className="slider-control">
            <span>💡</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={keywordWeight}
              onChange={(e) =>
                setKeywordWeight(Number(e.target.value))
              }
            />
            <span>🔍</span>
          </div>
          <div>
            Eingestellt: {Math.round(keywordWeight * 100)}% Keyword / {Math.round((1 - keywordWeight) * 100)}% Semantik
          </div>
          <div className="send-weight-wrapper">
            <button onClick={handleSendWeight} className="weight-button">
              Gewichtung senden
            </button>
            {showSuccess && <span className="checkmark">✔️</span>}
          </div>


        </div>

        <div className="section-label">Suchfelder:</div>
        <div className="checkbox-group">
          {availableFields.map((field) => (
            <label key={field} className="checkbox-label">
              <input
                type="checkbox"
                checked={requestConfig.selectedFields.includes(field)}
                onChange={() => toggleSearchField(field)}
              />
              <span>{field}</span>
            </label>
          ))}
        </div>

        {requestConfig.selectedFields.length > 0 && (
          <>
            <div className="section-label">Feldgewichtung:</div>
            {requestConfig.selectedFields.map((field) => (
              <div key={field} className="boost-row">
                <span className="boost-label">{field}</span>
                <input
                  type="number"
                  className="boost-input"
                  min="1"
                  value={requestConfig.fieldBoosts[field] ?? 1}
                  onChange={(e) => handleBoostChange(field, e.target.value)}
                />
              </div>
            ))}
          </>
        )}

        <div className="section-label">Filter Key-Value Paare:</div>
        {requestConfig.filters.map((filter, index) => (
          <div key={index} className="filter-pair">
            <input
              type="text"
              placeholder="Key"
              value={filter.key}
              onChange={(e) =>
                updateFilter("filters", index, e.target.value, filter.value)
              }
            />
            <input
              type="text"
              placeholder="Value"
              value={filter.value}
              onChange={(e) =>
                updateFilter("filters", index, filter.key, e.target.value)
              }
            />
            <button
              type="button"
              className="remove-filter-button"
              onClick={() => removeFilter("filters", index)}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => addFilter("filters")}
          className="add-filter-button"
        >
          + Filter hinzufügen
        </button>

        <div className="section-label">Not-Filter Key-Value Paare:</div>
        {requestConfig.notFilters.map((filter, index) => (
          <div key={index} className="filter-pair">
            <input
              type="text"
              placeholder="Key"
              value={filter.key}
              onChange={(e) =>
                updateFilter("notFilters", index, e.target.value, filter.value)
              }
            />
            <input
              type="text"
              placeholder="Value"
              value={filter.value}
              onChange={(e) =>
                updateFilter("notFilters", index, filter.key, e.target.value)
              }
            />
            <button
              type="button"
              className="remove-filter-button"
              onClick={() => removeFilter("notFilters", index)}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => addFilter("notFilters")}
          className="add-filter-button"
        >
          + Not-Filter hinzufügen
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
